import React, { useState, useEffect, useCallback } from 'react';
import apiService from '../../../apiService';
import { ACTIVITY_META, ACTIVITY_ORDER, fmtMoney, Card, KpiCard, Spinner, EXPENSE_CAT_LABELS, usePeriodControls } from '../financeShared';

export default function IncomeStatementTab({ currency, rate }) {
  const { params, qs, groupBy, controls } = usePeriodControls('1year', 'month');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const fmt = (n) => fmtMoney(n, currency, rate);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiService.get(`/admin/finance/income-statement?${qs()}`, { userType: 'admin' });
      if (res?.success) setData(res);
    } catch (e) { console.error('income-statement:', e); } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);
  useEffect(() => { load(); }, [load]);

  const byActivity = data?.byActivity || {};
  const expCats = data?.expensesByCategory || [];
  const timeline = data?.timeline || [];

  return (
    <>
      {controls()}
      {loading ? <Spinner /> : !data ? <p style={{ color: '#9AA0A8' }}>Aucune donnée.</p> : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1.25rem', marginBottom: '2rem' }}>
            <KpiCard label="Produits (CA)" value={fmt(data.revenue)} color="#10b981" border="#10b981" />
            <KpiCard label="Charges (OPEX)" value={fmt(data.totalExpenses)} color="#dc2626" border="#dc2626" />
            <KpiCard label="Résultat net" value={fmt(data.netResult)} color={data.netResult >= 0 ? '#10b981' : '#dc2626'} border="#FF7800" />
            <KpiCard label="Marge nette" value={data.netMarginPct == null ? '—' : `${data.netMarginPct}%`} color={data.netMarginPct >= 0 ? '#10b981' : '#dc2626'} border="#FF7800" />
          </div>

          {data.totalExpenses === 0 && (
            <div style={{ background: '#fef3c7', border: '1px solid #fde68a', borderRadius: '10px', padding: '0.85rem 1rem', marginBottom: '1.5rem', color: '#C9350F', fontSize: '0.85rem' }}>
              Aucune charge saisie sur la période : le résultat net égale le CA. Renseignez vos dépenses dans l'onglet <strong>Dépenses</strong> pour obtenir une marge nette réelle.
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
            <Card>
              <h3 style={{ marginBottom: '1rem', color: '#10b981', fontSize: '1rem', fontWeight: 700 }}>Produits</h3>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
                <tbody>
                  {ACTIVITY_ORDER.map(k => (
                    <tr key={k} style={{ borderBottom: '1px solid #F4F5F7' }}>
                      <td style={{ padding: '0.5rem 0', color: '#414852' }}>{ACTIVITY_META[k].label}</td>
                      <td style={{ padding: '0.5rem 0', textAlign: 'right', fontWeight: 600, color: '#1E2229' }}>{fmt(byActivity[k] || 0)}</td>
                    </tr>
                  ))}
                  <tr style={{ borderTop: '2px solid #E9EBEF' }}>
                    <td style={{ padding: '0.6rem 0', fontWeight: 700, color: '#10b981' }}>Total produits</td>
                    <td style={{ padding: '0.6rem 0', textAlign: 'right', fontWeight: 700, color: '#10b981' }}>{fmt(data.revenue)}</td>
                  </tr>
                </tbody>
              </table>
            </Card>

            <Card>
              <h3 style={{ marginBottom: '1rem', color: '#dc2626', fontSize: '1rem', fontWeight: 700 }}>Charges</h3>
              {expCats.length === 0 ? <p style={{ color: '#9AA0A8', fontSize: '0.85rem' }}>Aucune charge saisie.</p> : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
                  <tbody>
                    {expCats.map(c => (
                      <tr key={c.category} style={{ borderBottom: '1px solid #F4F5F7' }}>
                        <td style={{ padding: '0.5rem 0', color: '#414852' }}>{c.label || EXPENSE_CAT_LABELS[c.category] || c.category}</td>
                        <td style={{ padding: '0.5rem 0', textAlign: 'right', fontWeight: 600, color: '#1E2229' }}>{fmt(c.total)}</td>
                      </tr>
                    ))}
                    <tr style={{ borderTop: '2px solid #E9EBEF' }}>
                      <td style={{ padding: '0.6rem 0', fontWeight: 700, color: '#dc2626' }}>Total charges</td>
                      <td style={{ padding: '0.6rem 0', textAlign: 'right', fontWeight: 700, color: '#dc2626' }}>{fmt(data.totalExpenses)}</td>
                    </tr>
                  </tbody>
                </table>
              )}
            </Card>
          </div>

          <Card>
            <h3 style={{ marginBottom: '1.25rem', color: '#1E2229', fontSize: '1.05rem', fontWeight: 600 }}>Résultat ({groupBy === 'month' ? 'par mois' : 'par jour'})</h3>
            {timeline.length === 0 ? <p style={{ color: '#9AA0A8' }}>Aucune donnée.</p> : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr style={{ background: '#FFFFFF', borderBottom: '2px solid #E9EBEF' }}>
                    {['Période', 'Produits', 'Charges', 'Résultat'].map(h => (
                      <th key={h} style={{ padding: '0.75rem 1rem', textAlign: h === 'Période' ? 'left' : 'right', fontWeight: 700, color: '#414852', fontSize: '0.8rem', textTransform: 'uppercase' }}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {timeline.slice().reverse().map((row, idx) => (
                      <tr key={row.period} style={{ borderBottom: '1px solid #E9EBEF', background: idx % 2 === 0 ? '#fff' : '#FFFFFF' }}>
                        <td style={{ padding: '0.7rem 1rem', color: '#1E2229', fontWeight: 600 }}>{row.period}</td>
                        <td style={{ padding: '0.7rem 1rem', textAlign: 'right', color: '#10b981', fontWeight: 600 }}>{fmt(row.revenue)}</td>
                        <td style={{ padding: '0.7rem 1rem', textAlign: 'right', color: '#dc2626' }}>{fmt(row.expenses)}</td>
                        <td style={{ padding: '0.7rem 1rem', textAlign: 'right', color: row.net >= 0 ? '#10b981' : '#dc2626', fontWeight: 700 }}>{fmt(row.net)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}
    </>
  );
}
